// frontend/src/components/esim/ESIMOrderRow.js
import React from "react";
import {
  TableCell,
  TableRow,
  Box,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Message as MessageIcon,
  Cancel as CancelIcon,
  Pause as PauseIcon,
  PlayArrow as PlayArrowIcon,
  Block as BlockIcon,
  Download as DownloadIcon,
  Comment,
  Visibility,
} from "@mui/icons-material";
import ESIMStatusChip from "./ESIMStatusChip";
import { useTranslation } from "react-i18next";

const ESIMOrderRow = ({ order, onAction, onSendSMS, onViewDetails }) => {
  const { t } = useTranslation();

  const formatDataUsage = (used, total) => {
    const usedGB = (used / (1024 * 1024 * 1024)).toFixed(2);
    const totalGB = (total / (1024 * 1024 * 1024)).toFixed(2);
    return `${usedGB}GB / ${totalGB}GB`;
  };

  const formatDate = (date, transactionDate) => {
    if (!date) return "-";
    try {
      const formattedDate = new Date(date);
      return formattedDate.toString() !== "Invalid Date"
        ? formattedDate.toLocaleString()
        : "-";
    } catch (error) {
      console.error("Date formatting error:", error);
      return "-";
    }
  };

  // Implement the actual getAvailableActions function
  const getAvailableActions = (status) => {
    switch (status) {
      case "pending":
      case "GOT_RESOURCE":
        return ["cancel"];
      case "IN_USE":
        return ["suspend", "revoke", "sms"];
      case "SUSPENDED":
        return ["unsuspend", "revoke", "sms"];
      case "REVOKED":
      case "CANCELLED":
      case "EXPIRED":
      case "USED_UP":
        return [];
      default:
        return [];
    }
  };

  // Get available actions based on current status
  const availableActions = getAvailableActions(order.status);

  return (
    <TableRow hover>
      <TableCell>
        <ESIMStatusChip status={order.status} />
      </TableCell>
      <TableCell>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography>{order.profile?.name || order.user?.username}</Typography>
        </Box>
      </TableCell>
      <TableCell>{order.customerName || "-"}</TableCell>
      <TableCell>{order.customerEmail || "-"}</TableCell>
      <TableCell>{order.orderNo}</TableCell>
      <TableCell>{order.iccid || "-"}</TableCell>
      <TableCell>
        <Typography variant="body2">{order.package?.name}</Typography>
      </TableCell>
      <TableCell align="right">
        {formatDataUsage(order.orderUsage || 0, order.totalVolume)}
      </TableCell>
      <TableCell>
        {formatDate(order.createdAt, order.transaction?.date)}
      </TableCell>
      <TableCell align="right">{formatDate(order.expiryDate)}</TableCell>
      <TableCell>
        {order.notes ? (
          <Tooltip title={order.notes}>
            <IconButton size="small">
              <Comment fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          "-"
        )}
      </TableCell>
      <TableCell align="center">
        <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
          {/* View Details button */}
          <Tooltip title={t("common.actions.viewDetails")}>
            <IconButton size="small" onClick={() => onViewDetails(order)}>
              <Visibility />
            </IconButton>
          </Tooltip>

          {/* Action buttons - ensure availableActions is an array */}
          {Array.isArray(availableActions) &&
            availableActions.map((action) => {
              const ActionIcon = {
                suspend: PauseIcon,
                unsuspend: PlayArrowIcon,
                revoke: BlockIcon,
                sms: MessageIcon,
                cancel: CancelIcon,
              }[action];

              return (
                <Tooltip key={action} title={t(`esim.actions.${action}`)}>
                  <IconButton
                    size="small"
                    onClick={() =>
                      action === "sms"
                        ? onSendSMS(order)
                        : onAction(order, action)
                    }
                  >
                    <ActionIcon />
                  </IconButton>
                </Tooltip>
              );
            })}

          {/* Download QR Code button */}
          {order.qrCodeUrl && (
            <Tooltip title={t("esim.actions.downloadQR")}>
              <IconButton
                size="small"
                onClick={() => window.open(order.qrCodeUrl, "_blank")}
              >
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </TableCell>
    </TableRow>
  );
};

export default ESIMOrderRow;
